import { useField, useFormikContext } from 'formik';
import { flatten } from 'lodash-es';
import { useMemoryBankContext } from '../../../../../contexts/MemoryBankContext/MemoryBankContext';
import { AchievementCheckOperatorType } from '../../../../../dorian-shared/types/achievement/Achievement';
import { IAchievementModal } from '../../AchievementsModalTypes';
import { getFirstUserVisibleRewardIndex } from '../../utils';
import { useAchievementsModalContext } from '../../context/AchievementsModalContext';
import { getGroupedOptions } from './AchievementWhenUtils';

export function useAchievementWhen(rowIndex: number) {
  const { memories } = useMemoryBankContext();
  const { episodes } = useAchievementsModalContext();
  const { values } = useFormikContext<IAchievementModal[]>();

  const [checkVariableIdField, checkVariableIdFieldMeta, checkVariableIdFieldHelpers] = useField<number>(`[${rowIndex}].check.variableId`);
  const [checkOperatorField, checkOperatorFieldMeta, checkOperatorFieldHelpers] = useField<AchievementCheckOperatorType>(`[${rowIndex}].check.operator`);
  const [checkValueField, checkValueFieldMeta, checkValueFieldHelpers] = useField<string | number>(`[${rowIndex}].check.value`);

  // Get the index of the first user-visible reward (not HasAchievement)
  const userVisibleRewardIndex = getFirstUserVisibleRewardIndex(values[rowIndex]);
  const rewardIdx = userVisibleRewardIndex >= 0 ? userVisibleRewardIndex : 0;

  const [rewardContentIdField, rewardContentIdFieldMeta, rewardContentIdFieldHelpers] = useField<string>(`[${rowIndex}].rewards[${rewardIdx}].contentId`);
  const [rewardDelaySecField, rewardDelaySecFieldMeta, rewardDelaySecFieldHelpers] = useField<number>(`[${rowIndex}].rewards[${rewardIdx}].delaySec`);
  const [rewardTypeField, rewardTypeFieldMeta, rewardTypeFieldHelpers] = useField<number>(`[${rowIndex}].rewards[${rewardIdx}].type`);

  const availableWhenGroupedOptions = getGroupedOptions(memories, values, rowIndex, episodes);

  function getValue() {
    const flattenOptions = flatten(availableWhenGroupedOptions.map((option) => option.options));
    if (checkOperatorField.value === AchievementCheckOperatorType.AtLeast) {
      return flattenOptions.find((option) => String(option.value) === String(checkVariableIdField.value));
    }

    if (checkOperatorField.value === AchievementCheckOperatorType.HasCompleted) {
      return flattenOptions.find((option) => String(option.value) === String(checkValueField.value));
    }

    if (checkOperatorField.value === AchievementCheckOperatorType.Manual) {
      return flattenOptions.find((option) => option.type === 'manual');
    }

    return null;
  }

  return {
    value: getValue(),
    availableWhenGroupedOptions,
    check: {
      checkVariableIdField,
      checkVariableIdFieldMeta,
      checkVariableIdFieldHelpers,
      checkOperatorField,
      checkOperatorFieldMeta,
      checkOperatorFieldHelpers,
      checkValueField,
      checkValueFieldMeta,
      checkValueFieldHelpers,
    },
    reward: {
      rewardContentIdField,
      rewardContentIdFieldMeta,
      rewardContentIdFieldHelpers,
      rewardDelaySecField,
      rewardDelaySecFieldMeta,
      rewardDelaySecFieldHelpers,
      rewardTypeField,
      rewardTypeFieldMeta,
      rewardTypeFieldHelpers,
    },
  };
}
